// Org & /me lookups against robot-hub. All best-effort: any non-2xx returns
// null so callers can silently fall back (kiosk mode, expired token, etc.).

import { hubBaseUrl } from '@shared/lib/auditLogger';
import { isKiosk } from '@shared/lib/constants';
import { getAuthToken } from './authToken';

// Mirrors OrgPublic in robot-hub (app/db/models.py).
export interface Org {
  id: number;
  name: string;
  domain_name: string | null;
}

// Mirrors MePrincipal in robot-hub (app/db/models.py): /me may return either
// a user (cloud session) or a robot (kiosk via local auth proxy). The
// `type` discriminator lets callers tell them apart — only users have an
// `email`, only robots have a `name`, but both carry org context.
export interface UserPrincipal {
  type: 'user';
  id: number;
  cognito_id: string;
  email: string;
  org_id: number | null;
  org: Org | null;
}

export interface RobotPrincipal {
  type: 'robot';
  id: number;
  name: string;
  org_id: number | null;
  org: Org | null;
}

export type Me = UserPrincipal | RobotPrincipal;

export interface HubRobot {
  id: number;
  name: string;
}

export const VERNE_ORG_ID = 1;

async function authedJson<T>(path: string): Promise<T | null> {
  const token = getAuthToken();
  // Cloud requires a Cognito token; kiosk hits the local auth proxy which
  // injects credentials itself, so no Authorization header is sent.
  if (!token && !isKiosk) return null;
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    const r = await fetch(`${hubBaseUrl()}${path}`, { headers });
    if (!r.ok) return null;
    return (await r.json()) as T;
  } catch {
    return null;
  }
}

export function fetchMe(): Promise<Me | null> {
  return authedJson<Me>('/me');
}

// /orgs is scoped server-side: verne admins get every org, non-verne users
// get a single-element list with just their own org. So one endpoint covers
// both the switcher (verne) and the read-only badge (everyone else).
export function fetchOrgs(): Promise<Org[] | null> {
  return authedJson<Org[]>('/orgs');
}

export function fetchOrgRobots(orgId: number): Promise<HubRobot[] | null> {
  return authedJson<HubRobot[]>(`/org/${orgId}/robots`);
}

// Mirrors RobotPublicWithOrg in robot-hub.
export interface RobotWithOrg {
  id: number;
  name: string;
  org_id: number | null;
  org: Org | null;
}

// Looks up a robot by its serial (the URL's `robot_id` param). The hub
// auth-checks the caller against the robot, so non-verne users get null
// for robots outside their org — exactly what we want for the cross-org
// inference path.
export function fetchRobotByName(name: string): Promise<RobotWithOrg | null> {
  return authedJson<RobotWithOrg>(`/robot-by-name/${encodeURIComponent(name)}`);
}
